import { useEffect, useMemo, useState } from "react";
import { Modal, TextField } from "@mui/material";
import Button, { type ButtonOwnProps } from "@mui/material/Button";
import { atom, useAtom } from "jotai";

class IgnoredPromiseError extends Error {}

const getPromise = () => {
  let resolve: (b: boolean) => void = () => null;
  let reject: (e: Error) => void = () => null;

  const promise = new Promise<boolean>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });

  return {
    promise,
    resolve,
    reject,
  };
};

export const promiseAtom = atom(getPromise());

const initial = {
  isOpen: false,
  title: "Are you sure?",
  message: "",
  agree: "Agree",
  cancel: "Cancel",
  color: "error" as ButtonOwnProps["color"],
  promise: null as Promise<boolean> | null,
  confirmationText: undefined as string | undefined,
};

export const modalAtom = atom(initial);

export const useModal = () => {
  const [promise, setPromise] = useAtom(promiseAtom);
  const [modal, setModal] = useAtom(modalAtom);

  return useMemo(
    () => ({
      ...promise,
      ...modal,
      confirm(modal?: Partial<typeof initial>) {
        const p = getPromise();
        setPromise(p);

        setModal({
          ...initial,
          ...modal,
          isOpen: true,
        });

        return p.promise;
      },
    }),
    [modal, promise, setModal, setPromise]
  );
};

// used once in the app, in the AppProviders component
export const ConfirmationModal = () => {
  const [modal, setModal] = useAtom(modalAtom);
  const [promise] = useAtom(promiseAtom);
  const [inputValue, setInputValue] = useState("");

  const requiresConfirmationText = modal.confirmationText !== undefined;
  const isConfirmationTextValid =
    !requiresConfirmationText || inputValue === modal.confirmationText;

  // Reset input when modal opens/closes
  useEffect(() => {
    if (!modal.isOpen) {
      setInputValue("");
    }
  }, [modal.isOpen]);

  useEffect(() => {
    const cb = (event: WindowEventMap["unhandledrejection"]) => {
      if (event.reason instanceof IgnoredPromiseError) {
        event.preventDefault();
      }
    };

    window.addEventListener("unhandledrejection", cb);
    return () => {
      window.removeEventListener("unhandledrejection", cb);
    };
  }, []);

  return (
    <Modal
      className="flex items-center justify-center"
      open={modal.isOpen}
      onClose={() => {
        setModal({
          ...initial,
          isOpen: false,
        });
        promise.reject(new IgnoredPromiseError("Canceled"));
      }}
    >
      <div
        className="box-content flex w-96 flex-col gap-5 rounded bg-white dark:bg-background-paper dark:border dark:border-neutral-dark p-5 shadow-xl dark:shadow-2xl"
        data-test-id="confirmation-modal--content"
      >
        <h1 className="text-xl leading-3">{modal.title}</h1>
        <h1 className="leading-relaxes">{modal.message}</h1>

        {requiresConfirmationText && (
          <TextField
            data-test-id="confirmation-modal--confirmation-input"
            size="small"
            placeholder={`Type "${modal.confirmationText}" to confirm`}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
          />
        )}

        <div className="flex justify-between pt-5">
          <Button
            data-test-id="confirmation-modal--decline-action"
            onClick={() => {
              promise.reject(new IgnoredPromiseError("Canceled"));
              setModal({
                ...initial,
                isOpen: false,
              });
            }}
          >
            {modal.cancel}
          </Button>
          <Button
            data-test-id="confirmation-modal--approve-action"
            variant="outlined"
            color={modal.color}
            disabled={!isConfirmationTextValid}
            onClick={() => {
              promise.resolve(true);
              setModal({
                ...initial,
                isOpen: false,
              });
            }}
          >
            {modal.agree}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
